<script setup lang="ts">
import type { IBasicVideo, IEvent, IPlaylist } from "@/types";
import { EVENT_STATUS, PLAY_BASED_GAMES, VIDEO_TYPE } from "@/constants";

export interface ISharePayload {
  includeStats: boolean;
  expirationTime?: number | null;
  allowSaving?: boolean;
  sharedAngles?: string[];
}

const { t } = useI18n();
const toast = useToast();
const route = useRoute();
const segment = useSegment();

const props = defineProps<{
  modelValue?: boolean;
  video?: IEvent | IBasicVideo | null;
  playlist?: null | IPlaylist;
  shareTime?: number;
}>();

const emit = defineEmits(["update:modelValue"]);

const isOpen = useVModel(props, "modelValue", emit);

const onError = (err: unknown) => toast.error(parseErrorMessage(err, { t }));
const {
  state: shareUrl,
  isLoading,
  execute: onShareDataChanged,
} = useAsyncState(onShare, "", { onError, immediate: false, resetOnExecute: true, shallow: false });

const isSharedExternalEventsEnabled = computed(() => isFeatureEnabled("enable_event_shared_external_events"));
const isShareSpecificAnglesEnabled = computed(() => isFeatureEnabled("enable_event_share_specific_angles"));

const isEvent = computed(() => props.video?.type === VIDEO_TYPE.EVENT);
const isEventVod = computed(() => isEvent.value && (props.video as IEvent).status === EVENT_STATUS.VOD);
const isEventLive = computed(() => isEvent.value && (props.video as IEvent).status === EVENT_STATUS.LIVE);
const isPlayBasedEvent = computed(() => isEvent.value && PLAY_BASED_GAMES.includes((props.video as IEvent)?.sport_type ?? ""));
const isPlaylist = computed(() => Boolean(props.playlist));
const isDisableShareTime = computed<boolean>(() => isPlaylist.value || isEventLive.value);

const allowIncludeStats = computed(() => isEventVod.value && !isPlaylist.value);
const allowExpirationDate = computed(() => isEvent.value && !isPlaylist.value);
const allowSaving = computed(() => isSharedExternalEventsEnabled.value && !isPlaylist.value && isEventVod.value);
const allowShareAngles = computed(
  () =>
    isShareSpecificAnglesEnabled.value && isPlayBasedEvent.value && !isPlaylist.value && isEventVod.value && (props.video?.angles?.continuous?.size ?? 0) > 0
);

const eventAnglesOptions = computed(() =>
  allowShareAngles.value ? Array.from(props.video?.angles?.continuous?.keys() ?? []).map((k) => ({ label: capitalize(k), value: k })) : []
);

function getSharedVideoUrl(): string {
  const baseURL = `${window.location.origin}${useRuntimeConfig().app.baseURL}`;

  let sharedRoute = "";

  if (props.playlist) {
    sharedRoute = `user/content/?playlistId=${props.playlist.id}`;
  } else if (props.video && isEvent.value) {
    const editorRoute = `events/editor/${props.video.id}`;
    const eventRoute = `events/${props.video.id}`;
    const videoRouteBase = `events/${props.video.eventId}`;
    const videoRouteFull = `events/${props.video.eventId}?videoType=event&videoId=${props.video.id}`;
    sharedRoute = route.fullPath.includes(editorRoute)
      ? editorRoute
      : route.path.includes(videoRouteBase) && route.query.videoType === "event" && route.query.videoId === props.video.id
        ? videoRouteFull
        : eventRoute;
  } else {
    sharedRoute = `events/${props.video?.eventId}?videoType=${props.video?.type}&videoId=${props.video?.id}`;
  }

  return `${baseURL}${sharedRoute}`;
}

function onShare(payload?: ISharePayload): Promise<string | undefined> {
  if (!props.video && !props.playlist) return Promise.reject(new Error(t("errors.cannot_share_event")));
  if (props.video?.id === "demo") return Promise.reject(new Error(t("errors.cannot_share_demo_event")));

  const baseURL = getSharedVideoUrl();

  if (props.playlist) {
    return getPlaylistShareURL(props.playlist, { baseURL, accessToken: route.query.accessToken as string });
  }

  if (props.video) {
    return getVideoShareURL(props.video, {
      baseURL,
      accessToken: route.query.accessToken as string,
      includeStats: payload?.includeStats,
      expirationTime: payload?.expirationTime,
      allowSaving: payload?.allowSaving,
      sharedAngles: payload?.sharedAngles,
    });
  }

  return Promise.resolve("");
}

function handleShare(provider: string) {
  if (props.playlist) {
    segment.track("Playlist Shared", { ...formatTrackPlaylist(props.playlist), ...{ share_type: provider } });
  } else if (props.video) {
    segment.track("Video Shared", { ...formatTrackVideo(props.video), ...{ share_type: provider } });
  }
}

const heading = computed(() => (props.playlist ? t("labels.share_this_playlist") : t("labels.share_this_match")));
const description = computed(() => props.playlist?.name || props.video?.title);

watch(
  () => [props.video?.id, props.playlist?.id],
  () => onShareDataChanged(0, { includeStats: true }),
  { immediate: true }
);

function handleChange(payload?: ISharePayload) {
  onShareDataChanged(0, payload);
}

function closeModal() {
  isOpen.value = false;
}
</script>

<template>
  <UModal v-model="isOpen">
    <div class="p-4">
      <RLoadingOverlay v-if="isLoading" />

      <div class="mb-1 text-end">
        <UButton
          title="Close"
          variant="link-secondary"
          icon="close"
          class="size-5"
          @click="closeModal()"
        />
      </div>

      <!-- TEXT -->
      <div class="space-y-2 text-center text-black dark:text-white">
        <h1 class="text-sm font-bold" v-text="heading" />
        <p class="text-xs font-medium text-neutral-light-800" v-text="description" />
      </div>

      <div class="flex flex-col gap-6 md:p-6">
        <RShare
          :url="shareUrl || ''"
          :text="`Watch '${props.playlist?.name || props.video?.title}' at Pixellot!`"
          :time="isDisableShareTime ? undefined : props.shareTime"
          :allow-include-stats="allowIncludeStats"
          :allow-expiration-date="allowExpirationDate"
          :allow-saving="allowSaving"
          :allow-share-angles="allowShareAngles"
          :angles-options="eventAnglesOptions"
          has-email-provider
          @share="handleShare"
          @change="handleChange"
        />
      </div>
    </div>
  </UModal>
</template>
